import Component from '../../lib/component'
import Scroller from '../../lib/scroller'
import { RootComponent } from '../../main'

export default class TableStickyColumnComponent extends Component {
    static selector () {
        return '.table-sticky-column'
    }

    constructor (opts) {
        super(opts)

        this.touchThreshold = 12
        this.touchstartX = 0
        this.touchstartY = 0

        // Non-blocking rendering
        setTimeout(() => {
            if (!this.getTable())
                return

            this.addFeaturesToClassName()

            if (this.getTableContainer())
                this.Scroller
                    = new Scroller({ el: this.getTableContainer() })

            this.onDocumentScroll()
            this.render()
            this.listen()
        }, 0)
    }

    listen () {
        RootComponent.Scroller.state
            .on('update', this.onDocumentScroll.bind(this))

        this.on('resize', () => this.inViewport
                && this.width !== this.el.clientWidth
            ? this.render()
            : null)

        this.on('resize:debounced', () => {
            if (this.width === this.el.clientWidth)
                return

            this.onDocumentScroll()
            this.render()

            if (this.getFeature('touch'))
                this.Scroller.scrollTo({ x: 0, y: 0 })
        })

        if (!this.getFeature('touch'))
            return

        this.el
            .addEventListener('touchstart', this.onTouchStart.bind(this))

        this.el
            .addEventListener('touchmove', this.onTouchEnd.bind(this))

        this.el
            .addEventListener('touchend', this.onTouchEnd.bind(this))

        if (this.getTableContainer())
            this.getTableContainer()
                .addEventListener('blur', () =>
                    this.Scroller.scrollTo({ x: 0, y: 0 }))
    }

    onTouchStart (e) {
        if (!this.inViewport)
            return

        this.touchstartX = e.changedTouches[0].screenX
        this.touchstartY = e.changedTouches[0].screenY
    }

    onTouchEnd (e) {
        if (!this.inViewport)
            return

        const tableContainer = this.getTableContainer()

        if (!tableContainer
                || tableContainer.scrollWidth <= tableContainer.clientWidth
                || tableContainer.classList.contains('focus-visible')
                || this.Scroller.scrollingTo)
            return

        const xDiff = e.changedTouches[0].screenX - this.touchstartX
        const yDiff = e.changedTouches[0].screenY - this.touchstartY

        const getDiffAmount = (diff) => diff < 0
            ? diff * -1
            : diff

        if (getDiffAmount(xDiff) < this.touchThreshold
                || getDiffAmount(yDiff) > getDiffAmount(xDiff))
            return

        const scrollArea = tableContainer.scrollWidth
            - tableContainer.clientWidth

        const pageW = this.el.clientWidth - this.getStickyColumnWidth()

        const scrollX = this.Scroller.state.get('scrollX')

        if ((xDiff > 0
                    && scrollX === 0)
                || (xDiff < 0
                    && scrollX === scrollArea))
            return

        const scrollToX = scrollX + (xDiff > 0
            ? -pageW
            : pageW)

        const speed = this.el.clientWidth < 576
            ? 0.7
            : 0.8

        this.Scroller.scrollTo({
            x: scrollToX,
            y: 0
        }, speed)
    }

    onDocumentScroll (e) {
        const bounds = this.el.getBoundingClientRect()

        const threshold = window.innerHeight

        const inViewport = (bounds.bottom >= 0
                && bounds.bottom <= threshold)
            || (bounds.top >= 0
                && bounds.top <= threshold)

        if (!inViewport
                && (this.inViewport
                    || !('inViewport' in this)))
            this.inViewport = false

        if (inViewport && !this.inViewport)
            this.inViewport = true
    }

    getComputedStyleInt (el, prop) {
        return Number((window.getComputedStyle(el)[prop] || '0')
            .replace(/[a-z]/ig, ''))
    }

    getStickyColumnBg () {
        return this.el.querySelector(`
            ${TableStickyColumnComponent.selector()}-scrollable-bg`)
    }

    getTable () {
        return this.el.querySelector('table')
    }

    getTableContainer () {
        return this.el.querySelector(`
            ${TableStickyColumnComponent.selector()}-container`)
    }

    getTablePlaceholder () {
        return this.el.querySelector(`
            ${TableStickyColumnComponent.selector()}-placeholder`)
    }

    getTouchProgressBar () {
        return this.el.querySelector(`${TableStickyColumnComponent
            .selector()}-scrollable-progress-bar`)
    }

    getStickyColumnWidth () {
        if (!RootComponent.getFeature('csspositionsticky'))
            return 0

        const stickyColumnWidth = Array.from(this.el
            .querySelectorAll('.table-row-data th[scope="row"]:first-child'))
            .map(el => el.clientWidth)
            .sort()
            .reverse()[0] || 0

        return stickyColumnWidth
            ? stickyColumnWidth
                + (window.innerWidth === this.el.clientWidth
                    ? 21.5
                    : 1.5)
            : 0
    }

    getTableContainerScrollbarWidth () {
        const inner = document.createElement('p')
        const outer = document.createElement('div')

        inner.style.width = '100%'
        inner.style.height = '200px'

        outer.style.position = 'absolute'
        outer.style.top = '-150px'
        outer.style.left = '-200px'
        outer.style.width = '200px'
        outer.style.height = '150px'
        outer.style.visibility = 'hidden'
        outer.style.overflow = 'scroll'

        outer.appendChild(inner)
        document.body.appendChild(outer)

        const width = outer.offsetWidth - inner.clientWidth
        document.body.removeChild(outer)

        return width
    }

    renderTouchProgressBar () {
        if (!this.getFeature('touch'))
            return

        const scrollableClassname = `${TableStickyColumnComponent
            .selector()
            .replace(/^\./, '')}-scrollable`

        const tableContainer = this.getTableContainer()

        if (!this.el.classList.contains(scrollableClassname))
            return tableContainer.getAttribute('tabindex')
                ? tableContainer.removeAttribute('tabindex')
                : null

        if (tableContainer.getAttribute('tabindex') !== '0')
            tableContainer.setAttribute('tabindex', '0')

        if (!this.getTouchProgressBar()) {
            const progress = document.createElement('div')

            progress.className = `${scrollableClassname}-progress`
            progress.innerHTML
                = `<div class="${scrollableClassname}-progress-bar"></div>`

            this.el.appendChild(progress)
        }

        if (this.getTouchProgressBar()) {
            const scrollArea = tableContainer.scrollWidth
                - tableContainer.clientWidth

            const pageW = this.el.clientWidth - this.getStickyColumnWidth()

            this.getTouchProgressBar()
                .style.width = `${(1 / Math.ceil(scrollArea / pageW)) * 100}%`
        }
    }

    renderScrollable () {
        const scrollableClassname = `${TableStickyColumnComponent
            .selector()
            .replace(/^\./, '')}-scrollable`

        if (!this.el.classList.contains(scrollableClassname))
            return

        const stickyColumnBg = this.getStickyColumnBg()
        const stickyColumnWidth = this.getStickyColumnWidth()

        if (!stickyColumnBg
                || !stickyColumnWidth)
            return

        if (stickyColumnBg.clientWidth !== stickyColumnWidth)
            stickyColumnBg.style.width = `${stickyColumnWidth}px`

        const scrollbarWidthProperty
            = '--table-sticky-column-scrollbar-width'

        if (!document.documentElement.style
            .getPropertyValue(
                scrollbarWidthProperty))
            document.documentElement
                .style.setProperty(scrollbarWidthProperty,
                    `${this.getTableContainerScrollbarWidth()}px`)

        if (!stickyColumnBg.style.bottom)
            stickyColumnBg.style.bottom
                = `var(${scrollbarWidthProperty})`

        Array.from(this.getTable()
            .querySelectorAll('.table-row-colgroup th[scope="colgroup"] span'))
            .filter(el => el.style.left !== `${stickyColumnWidth}px`)
            .forEach(el => {
                el.style.left = `${stickyColumnWidth}px`
            })
    }

    render () {
        const table = this.getTable()

        const tableContainer = this.getTableContainer()

        const isSizeChange = this.width !== this.el.clientWidth

        const scrollableClassname = `${TableStickyColumnComponent
            .selector()
            .replace(/^\./, '')}-scrollable`

        let isScrollable = this.el.classList
            .contains(scrollableClassname)

        let isScrollableChange = false

        if (isSizeChange) {
            this.width = this.el.clientWidth

            if (this.props.sizing !== 'true')
                this.props.sizing = true

            if (!table.style.minWidth)
                table.style.minWidth = '0px'

            isScrollable = table.clientWidth
                > tableContainer.clientWidth
                    - (window.innerWidth === this.el.clientWidth
                        ? this.getComputedStyleInt(tableContainer,
                            'paddingLeft') * 2
                        : 0)

            if (this.props.sizing !== 'false')
                this.props.sizing = false

            table.style.minWidth = ''

            if (isScrollable
                    && !this.el.classList.contains(scrollableClassname)) {
                this.el.classList.add(scrollableClassname)
                isScrollableChange = true
            }

            if (!isScrollable
                    && this.el.classList.contains(scrollableClassname)) {
                this.el.classList.remove(scrollableClassname)
                isScrollableChange = true
            }
        }

        if (isScrollable
                && (isScrollableChange
                    || isSizeChange))
            this.renderScrollable()

        this.renderTouchProgressBar()
    }
}
